// Auth guard for super-admin routes.
//
// admin_session is an httpOnly cookie, so the SPA can't inspect it directly.
// We probe /admin/users/me — admin-api lets that route through even before
// TOTP is enabled, which lets us distinguish the three states the dashboard
// needs to react to:
//   401          → no session  → redirect to /login
//   200 + totp_enabled=false → cookie valid but enrolment incomplete → /setup-totp
//   200 + totp_enabled=true  → fully authed, render children
//
// Without this, an unauthenticated visit to `/` rendered TenantListPage,
// which silently 401'd on its own API call and left a blank shell — the
// "looks like I'm already logged in" misimpression flagged in the
// 2026-05-11 deploy session (M2 step 1).

import { useEffect, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { adminFetch } from "../lib/api.js";

type AuthState =
  | { kind: "checking" }
  | { kind: "authed" }
  | { kind: "needs_totp_setup" }
  | { kind: "unauthed" };

interface MeResponse {
  totp_enabled?: boolean;
}

export function RequireAdminAuth({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ kind: "checking" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await adminFetch<MeResponse>("/admin/users/me");
        if (cancelled) return;
        setState(me.totp_enabled ? { kind: "authed" } : { kind: "needs_totp_setup" });
      } catch {
        // Network failures and non-2xx responses both land here. Fail-safe to
        // /login — user can retry from there.
        if (!cancelled) setState({ kind: "unauthed" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.kind === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Checking session…
      </div>
    );
  }
  if (state.kind === "unauthed") {
    return <Navigate to="/login" replace />;
  }
  if (state.kind === "needs_totp_setup") {
    return <Navigate to="/setup-totp" replace />;
  }
  return <>{children}</>;
}
